import { useEffect, useState } from "react";
import type { DateRange } from "@act-group/act-ui-library";
import { useFetch } from "./useFetch";
import { fetchVerificationsSummary } from "../services/verificationsService";
import { getAvailableJurisdictions } from "../types/verification";

const DATE_FORMAT = "YYYY-MM-DD";

export interface UseVerificationsSummaryOptions {
  dateRange: DateRange;
  /** Jurisdiction name to scope the summary to; omit for all jurisdictions. */
  jurisdiction?: string;
}

export function useVerificationsSummary({
  dateRange,
  jurisdiction,
}: UseVerificationsSummaryOptions) {
  const { data, isLoading, error, fetchData } = useFetch(fetchVerificationsSummary);
  const [availableJurisdictions, setAvailableJurisdictions] = useState<string[]>([]);
  // isLoading starts false before the fetch effect below has ever run, so it
  // can't tell "haven't started yet" apart from "finished" — callers gating on
  // the *first* response landing (e.g. the Dashboard, before it fetches the
  // verifications list) need this instead.
  const [hasResolved, setHasResolved] = useState(false);

  useEffect(() => {
    fetchData({
      fromDate: dateRange.start.format(DATE_FORMAT),
      toDate: dateRange.end.format(DATE_FORMAT),
      jurisdiction,
    })
      .then((result) => {
        // Derived from the same resolved response that flips hasResolved, in
        // the same callback — not a separate effect watching `data` — so a
        // caller gating on hasResolved never sees a render where it's true but
        // availableJurisdictions hasn't caught up yet (that gap otherwise lets
        // a "no jurisdictions" read through as real when it's just stale).
        //
        // The jurisdiction filter's own options come from this response, so
        // they're only taken from an unfiltered request: a jurisdiction-scoped
        // response may legitimately narrow the list to the selected one, and
        // rebuilding the tabs from that would strand the user on a single
        // option with no way back.
        if (!jurisdiction) {
          const available = getAvailableJurisdictions(result);
          if (available.length > 0) setAvailableJurisdictions(available);
        }
      })
      .catch(() => {
        // Failure is already captured in `error` state via useFetch; swallow
        // here so a network error doesn't surface as an unhandled rejection.
      })
      .finally(() => setHasResolved(true));
  }, [fetchData, dateRange, jurisdiction]);

  return {
    activity: data?.activity ?? null,
    devices: data?.devices ?? null,
    declineReasons: data?.declineReasons ?? null,
    availableJurisdictions,
    hasResolved,
    isLoading,
    error,
  };
}
